<template>
  <div class="w-[60%] md:w-[80%] xl:w-[85.71%]">
    <div class="flex">
      <!-- MARK: Desktop -->
      <div class="hidden mx-auto xl:ml-6 xl:flex xl:w-xl">
        <div
          v-for="({ to, label, target, ...link }, index) in links"
          :key="label"
          class="cursor-pointer group"
          @click="link.items && toggleSubmenu(index)"
          @mouseenter="link.items && (openSubmenu = index)"
          @mouseleave="closeSubmenu"
        >
          <div class="pt-2">
            <NuxtLink
              :to
              :target
              class="p-2 hover:underline hover:decoration-1 hover:underline-offset-12 hover:decoration-cred"
            >
              {{ label }}
            </NuxtLink>
          </div>
          <div
            v-if="link.items"
            class="absolute pt-6 z-50"
            :class="openSubmenu === index ? 'visible' : 'invisible'"
          >
            <div class="p-2 text-left bg-cblack rounded-2xl">
              <div
                v-for="item in link.items"
                :key="item.label"
                class="w-full rounded-2xl bg-cblack team"
              >
                <NuxtLink
                  :to="item.to"
                  :target="item.target"
                  class="flex items-center w-full gap-4 p-2 group2 hover:underline hover:decoration-1 hover:underline-offset-4 hover:decoration-cred"
                >
                  <div class="flex flex-1 gap-2">
                    {{ item.label }}
                  </div>
                  <Icon
                    name="heroicons:chevron-right"
                    size="18"
                    class="text-cred"
                  />
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-end flex-1">
        <PublicNavigationShoppingCartButton
          v-if="isShppingCartEnabled"
          class="mr-2"
          :basket-items-count="basketItemsQuantity"
          @basket-click="onClickShoppingCart()"
        />

        <div class="hidden md:block">
          <VButton
            trailing-icon="i-heroicons-arrow-right"
            :to="'/plattform'"
            :size="Size.sm"
            class="flex justify-center px-5"
          >
            {{ $t('toThePortal') }}
          </VButton>
        </div>

        <!-- MARK: Mobile  -->
        <div class="xl:hidden ml-2">
          <UPopover
            v-model:open="isMobileMenuOpen"
            :content="{
              align: 'end',
              side: 'bottom',
              sideOffset: 8,
            }"
            :ui="{
              content:
                'p-2 shadow-2xl min-w-52 ring-1 ring-black/5 bg-cblack xl:hidden',
            }"
          >
            <button
              id="mobile-menu-button"
              type="button"
              class="flex justify-center w-full p-1 text-sm font-medium text-white ring ring-white rounded-md focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-cblack"
            >
              <UIcon
                name="i-heroicons-bars-3-solid"
                class="text-white size-7"
                aria-hidden="true"
              />

              <span class="sr-only">Menu</span>
            </button>

            <template #content>
              <UNavigationMenu
                color="neutral"
                variant="link"
                :items="mobileLinks"
                class="min-w-60 w-full"
                orientation="vertical"
                external-icon="false"
                :ui="{
                  link: 'text-white hover:text-gray-600 text-base font-medium data-[active]:bg-gray-700 data-[active]:hover:text-white rounded-md p-2 cursor-pointer',
                }"
              />
              <div class="md:hidden mt-2">
                <VButton
                  trailing-icon="i-heroicons-arrow-right"
                  :to="'/plattform'"
                  :size="Size.sm"
                  class="w-full justify-center"
                >
                  {{ $t('toThePortal') }}
                </VButton>
              </div>
            </template>
          </UPopover>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Size } from '@/types/enums'
import type { DropdownMenuItem } from '@nuxt/ui'

const emits = defineEmits(['shopping-slider'])

const { t } = useI18n()
const { fetchStoredVehicles } = useVehicleRequestStorage()
const basketItemsQuantity = ref<number>((await fetchStoredVehicles()).size)
const isMobileMenuOpen = ref(false)

const openSubmenu = ref<number | null>(null)

function toggleSubmenu(index: number) {
  openSubmenu.value = openSubmenu.value === index ? null : index
}

function closeSubmenu() {
  openSubmenu.value = null
}

const isShppingCartEnabled = false

const categories: DropdownMenuItem[] = useSortedCategories().map(
  (category) => ({
    label: category.title,
    to:
      useRuntimeConfig().public.ENV === 'production'
        ? `/request-quote?category=${category.slug}`
        : `/offers?category=${category.slug}`,
    target: '_self',
  })
)

const mobileLinks: DropdownMenuItem[][] = [
  [...categories],
  [
    {
      label: t('about'),
      to: '/ueber-uns',
      target: '_self',
    },
    {
      label: t('contact'),
      to: '/kontakt',
      target: '_self',
    },
    {
      label: t('blog'),
      to: '/blog',
      target: '_self',
    },
    {
      label: t('career'),
      to: 'https://colonia-technologies-gmbh.jobs.personio.de/',
      target: '_blank',
    },
    {
      label: t('rentOut'),
      to: '/vermieten',
      target: '_self',
    },
  ],
]

const links: DropdownMenuItem[] = [
  {
    label: t('rentIn', 2),
    items: categories,
  },
  {
    label: t('rentOut'),
    to: '/vermieten',
    target: '_self',
  },
  {
    label: t('theFooter.company'),
    items: [
      {
        label: t('career'),
        to: 'https://colonia-technologies-gmbh.jobs.personio.de/',
        target: '_blank',
      },
      {
        label: t('about'),
        to: '/ueber-uns',
        target: '_self',
      },
      {
        label: t('blog'),
        to: '/blog',
        target: '_self',
      },
      {
        label: t('contact'),
        to: '/kontakt',
        target: '_self',
      },
    ],
  },
]

function onClickShoppingCart() {
  useNuxtApp().$trackEvent('shopping_cart.opened', {
    count: basketItemsQuantity.value,
  })
  emits('shopping-slider')
}
</script>
